import axios from 'axios';
import { API_BASE_URL } from '../config/api';

/**
 * Login token handling for every API call.
 *
 * installAuthTransport() runs once at startup. It wraps window.fetch and adds an
 * axios interceptor so requests to the COOL API carry "Authorization: Bearer
 * <token>" without each component having to remember. It also stores the token
 * that login.php / google-login.php return, and fires "cool:auth-expired" when
 * the API says the session is no longer valid.
 */

const TOKEN_KEY = 'coolAuthToken';
const LOGIN_ENDPOINTS = /\/(login|google-login)\.php(\?|$)/;
export const AUTH_EXPIRED_EVENT = 'cool:auth-expired';

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage unavailable — requests just go without a token */
  }
}

export function clearAuthToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

const apiOrigin = (() => {
  try {
    return new URL(API_BASE_URL, window.location.href).origin;
  } catch {
    return window.location.origin;
  }
})();

/** True only for COOL backend URLs, so the token never goes to Google or other hosts. */
function isApiUrl(rawUrl) {
  if (!rawUrl) return false;
  try {
    const url = new URL(String(rawUrl), window.location.href);
    return url.origin === apiOrigin && url.pathname.includes('/backend/');
  } catch {
    return false;
  }
}

function notifyExpired() {
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

let installed = false;

export function installAuthTransport() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' || input instanceof URL ? String(input) : input?.url;
    if (!isApiUrl(url)) {
      return nativeFetch(input, init);
    }

    const token = getAuthToken();
    let request = input;
    let options = init;
    if (token) {
      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      options = { ...init, headers };
    }

    const response = await nativeFetch(request, options);

    if (LOGIN_ENDPOINTS.test(url)) {
      // Store the new token before the caller continues, so its next request is signed.
      const data = await response.clone().json().catch(() => null);
      if (data?.success && data.token) {
        setAuthToken(data.token);
      }
    } else if (response.status === 401) {
      notifyExpired();
    }
    return response;
  };

  axios.interceptors.request.use((config) => {
    const token = getAuthToken();
    const fullUrl = config.baseURL && !/^https?:/i.test(config.url || '')
      ? `${config.baseURL.replace(/\/+$/, '')}/${String(config.url || '').replace(/^\/+/, '')}`
      : config.url;
    if (token && isApiUrl(fullUrl)) {
      config.headers = config.headers || {};
      if (typeof config.headers.set === 'function') {
        if (!config.headers.has('Authorization')) config.headers.set('Authorization', `Bearer ${token}`);
      } else if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401 && isApiUrl(error.config?.url)) {
        notifyExpired();
      }
      return Promise.reject(error);
    }
  );
}

/** Revoke the token on the server (best effort) and forget it locally. */
export async function endAuthSession() {
  const token = getAuthToken();
  clearAuthToken();
  if (!token) return;
  try {
    await fetch(`${API_BASE_URL}/logout.php`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    /* offline — the token still expires on its own */
  }
}
